import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
  Box,
  Button,
  CircularProgress,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { OssStatusChip } from "../OssStatusChip";
import { actionType } from "../../constants/labels";
import { isMockAction } from "../../utils/actionQueueUtils";

const severityColor = {
  CRITICAL: "#991b1b",
  HIGH: "#ef4444",
  ATTENTION: "#f59e0b",
};

const headCells = ["조치 구분", "주문/품목", "거래처", "요청 납기", "다음 확인", "일정 상태", "기한 상태", "처리"];

const cellSx = {
  py: 1.85,
  px: 2,
};

export const OssActionQueueList = ({
  items,
  loading,
  pagination,
  page,
  onPageChange,
  onProcess,
  onOpenDetail,
}) => (
  <>
    {loading ? (
      <Box sx={{ flex: 1, minHeight: 0, display: "grid", placeItems: "center" }}>
        <CircularProgress size={32} />
      </Box>
    ) : items.length === 0 ? (
      <Box sx={{ flex: 1, minHeight: 0, display: "grid", placeItems: "center", color: "#64748b" }}>
        조치가 필요한 주문이 없습니다.
      </Box>
    ) : (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          p: 0,
          bgcolor: "#f8fafc",
        }}
      >
        <TableContainer
          sx={{
            height: "100%",
            overflow: "auto",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            bgcolor: "#fff",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: 8, height: 8 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 999,
              backgroundColor: "#cbd5e1",
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "#f8fafc" },
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 1320 }}>
            <TableHead>
              <TableRow>
                {headCells.map((label) => (
                  <TableCell
                    key={label}
                    align="center"
                    sx={{
                      py: 1.35,
                      px: 1.6,
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                      color: "#475569",
                      fontSize: 12.8,
                      fontWeight: "800 !important",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const tone = severityColor[item.severity] || severityColor[item.risk_level] || "#6c5ce7";

                return (
                  <TableRow
                    key={`${item.order_line_id}-${item.exception_type || item.exception_title}`}
                    hover
                    sx={{
                      bgcolor: "#fff",
                      height: 82,
                      "&:nth-of-type(even)": {
                        bgcolor: "#fbfcff",
                      },
                      "&:hover": {
                        bgcolor: "#f8fafc",
                      },
                      "& td": {
                        borderBottom: "1px solid #e5e7eb",
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        ...cellSx,
                        borderLeft: `4px solid ${tone}`,
                        width: 310,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 1,
                          py: 0.45,
                          mb: 0.6,
                          borderRadius: "999px",
                          bgcolor: `${tone}12`,
                          color: tone,
                          border: `1px solid ${tone}36`,
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: "900 !important", lineHeight: 1.15 }}>
                          {item.exception_title || actionType[item.exception_type] || "확인 필요"}
                        </Typography>
                      </Box>
                      <Typography sx={{ mt: 0.45, fontSize: 12.6, color: "#64748b", lineHeight: 1.45 }}>
                        {item.exception_message || "주문 일정 확인 또는 생산계획 검토가 필요합니다."}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, width: 340 }}>
                      <Typography noWrap sx={{ fontSize: 13.8, fontWeight: "800 !important", color: "#111827", lineHeight: 1.35 }}>
                        {item.order_no || "-"}
                      </Typography>
                      <Typography noWrap sx={{ mt: 0.5, fontSize: 12.9, color: "#475569", lineHeight: 1.35 }}>
                        {item.item_name_snapshot || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, width: 150 }}>
                      <Typography noWrap sx={{ fontSize: 13.7, color: "#111827", lineHeight: 1.35 }}>
                        {item.customer_name || "-"}
                      </Typography>
                      {item.site_name && (
                        <Typography noWrap sx={{ mt: 0.45, fontSize: 12.5, color: "#64748b", lineHeight: 1.35 }}>
                          {item.site_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, width: 130 }}>
                      <Typography sx={{ fontSize: 14.3, fontWeight: "800 !important", color: "#111827", lineHeight: 1.35 }}>
                        {item.requested_due_date || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, width: 130 }}>
                      <Typography sx={{ fontSize: 14.1, fontWeight: "700 !important", color: "#111827", lineHeight: 1.35 }}>
                        {item.next_check_date || item.check_required_date || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, width: 120 }}>
                      <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
                        <OssStatusChip value={item.schedule_status} />
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, width: 120 }}>
                      <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
                        <OssStatusChip type="risk" value={item.risk_level} />
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellSx, width: 160 }}>
                      <Stack direction="row" spacing={0.6} justifyContent="flex-end" alignItems="center" flexWrap="nowrap">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FactCheckOutlinedIcon />}
                          onClick={() => onProcess(item)}
                          disabled={isMockAction(item)}
                          sx={{ minWidth: 76, height: 34, fontWeight: "800 !important" }}
                        >
                          처리
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          endIcon={<OpenInNewRoundedIcon />}
                          onClick={() => onOpenDetail(item.order_id)}
                          sx={{ minWidth: 98, height: 34, fontWeight: "800 !important" }}
                        >
                          주문 보기
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )}

    <Box
      sx={{
        px: 2,
        py: 1.2,
        mt: 1.25,
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        display: "flex",
        justifyContent: "flex-end",
        flexShrink: 0,
        bgcolor: "#fbfbfe",
      }}
    >
      <Pagination
        page={pagination.page || page}
        count={pagination.totalPages || 1}
        onChange={(_, next) => onPageChange(next)}
        color="primary"
        size="small"
      />
    </Box>
  </>
);
